/**
 * RA bill calculation router — mandatory CPWD fallback.
 * Existing bills without a CONFIGURABLE template snapshot always use computeCpwdRABillAmounts.
 */

import {
  computeCpwdRABillAmounts,
  extractCommercialInputs,
  toLegacyFormulaShape,
  nz,
} from './cpwdRABillFormula';

const applyRounding = (value, method = 'ROUND', decimalPlaces = 0) => {
  const n = nz(value);
  const places = Math.max(0, Number(decimalPlaces) || 0);
  const factor = 10 ** places;
  const m = String(method || 'ROUND').toUpperCase();
  if (m === 'FLOOR') return Math.floor(n * factor) / factor;
  if (m === 'CEIL') return Math.ceil(n * factor) / factor;
  if (m === 'NONE') return n;
  return Math.round(n * factor) / factor;
};

const isTruthy = (v) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.toLowerCase() === 'true' || v === '1';
  return nz(v) !== 0;
};

const evaluateAst = (ast, ctx = {}) => {
  const current = ctx.currentValues || {};
  const previous = ctx.previousValues || {};
  const project = ctx.projectValues || {};
  const agreement = ctx.agreementValues || {};
  const department = ctx.departmentValues || {};
  const cumulative = ctx.cumulativeValues || {};

  const evalNode = (node) => {
    if (node == null) return 0;
    if (typeof node === 'number') return node;
    if (typeof node !== 'object') return 0;
    if (node.type === 'CONSTANT') return nz(node.value);
    if (node.type === 'FIELD') return nz(current[node.fieldCode]);
    if (node.type === 'PREVIOUS_RA_FIELD') {
      const v = previous[node.fieldCode];
      if (v == null || v === '') return nz(node.fallback);
      return nz(v);
    }
    if (node.type === 'CUMULATIVE_FIELD') return nz(cumulative[node.fieldCode] ?? node.fallback);
    if (node.type === 'PROJECT_FIELD') return nz(project[node.fieldCode] ?? node.fallback);
    if (node.type === 'AGREEMENT_FIELD') return nz(agreement[node.fieldCode] ?? node.fallback);
    if (node.type === 'DEPARTMENT_FIELD') return nz(department[node.fieldCode] ?? node.fallback);

    const op = String(node.operator || '').toUpperCase();
    const args = Array.isArray(node.arguments) ? node.arguments.map(evalNode) : [];
    switch (op) {
      case 'ADD':
      case 'SUM':
        return args.reduce((s, a) => s + nz(a), 0);
      case 'SUBTRACT':
        return args.length ? args.slice(1).reduce((s, a) => s - nz(a), nz(args[0])) : 0;
      case 'MULTIPLY':
        return args.length ? args.reduce((s, a) => s * nz(a), 1) : 0;
      case 'DIVIDE': {
        if (args.length < 2) return 0;
        let out = nz(args[0]);
        for (let i = 1; i < args.length; i += 1) {
          const d = nz(args[i]);
          if (d === 0) return 0;
          out /= d;
        }
        return out;
      }
      case 'MIN':
        return args.length ? Math.min(...args.map(nz)) : 0;
      case 'MAX':
        return args.length ? Math.max(...args.map(nz)) : 0;
      case 'ABS':
        return Math.abs(nz(args[0]));
      case 'ROUND':
        return applyRounding(args[0], 'ROUND', node.decimalPlaces ?? 0);
      case 'FLOOR':
        return applyRounding(args[0], 'FLOOR', node.decimalPlaces ?? 0);
      case 'CEIL':
        return applyRounding(args[0], 'CEIL', node.decimalPlaces ?? 0);
      case 'PERCENTAGE':
        return nz(args[0]) * (nz(args[1]) / 100);
      case 'IF': {
        const cond = node.condition != null ? evalNode(node.condition) : args[0];
        const thenV = node.then != null ? evalNode(node.then) : args[1];
        const elseV = node.else != null ? evalNode(node.else) : (args[2] ?? 0);
        return isTruthy(cond) ? thenV : elseV;
      }
      case 'AND':
        return args.every(isTruthy) ? 1 : 0;
      case 'OR':
        return args.some(isTruthy) ? 1 : 0;
      case 'NOT':
        return isTruthy(args[0]) ? 0 : 1;
      case 'GREATER_THAN':
        return nz(args[0]) > nz(args[1]) ? 1 : 0;
      case 'GREATER_THAN_OR_EQUAL':
        return nz(args[0]) >= nz(args[1]) ? 1 : 0;
      case 'LESS_THAN':
        return nz(args[0]) < nz(args[1]) ? 1 : 0;
      case 'LESS_THAN_OR_EQUAL':
        return nz(args[0]) <= nz(args[1]) ? 1 : 0;
      case 'EQUAL':
        return nz(args[0]) === nz(args[1]) ? 1 : 0;
      case 'NOT_EQUAL':
        return nz(args[0]) !== nz(args[1]) ? 1 : 0;
      default:
        return 0;
    }
  };
  return evalNode(ast);
};

const collectSameBillFieldRefs = (node, out = new Set()) => {
  if (!node || typeof node !== 'object') return out;
  if (node.type === 'FIELD' && node.fieldCode) out.add(String(node.fieldCode));
  (node.arguments || []).forEach((a) => collectSameBillFieldRefs(a, out));
  ['left', 'right', 'condition', 'then', 'else'].forEach((k) => {
    if (node[k]) collectSameBillFieldRefs(node[k], out);
  });
  return out;
};

const topoOrder = (fields) => {
  const codes = fields.map((f) => String(f.fieldCode));
  const adjacency = new Map(codes.map((c) => [c, new Set()]));
  fields.forEach((f) => {
    if (!f.formulaJson) return;
    collectSameBillFieldRefs(f.formulaJson).forEach((ref) => {
      if (adjacency.has(String(f.fieldCode)) && adjacency.has(ref)) {
        adjacency.get(String(f.fieldCode)).add(ref);
      }
    });
  });
  const visiting = new Set();
  const visited = new Set();
  const order = [];
  let cycle = null;
  const visit = (node, stack) => {
    if (cycle) return;
    if (visited.has(node)) return;
    if (visiting.has(node)) {
      const idx = stack.indexOf(node);
      cycle = idx >= 0 ? [...stack.slice(idx), node] : [node];
      return;
    }
    visiting.add(node);
    stack.push(node);
    (adjacency.get(node) || new Set()).forEach((d) => visit(d, stack));
    stack.pop();
    visiting.delete(node);
    visited.add(node);
    order.push(node);
  };
  codes.forEach((c) => visit(c, []));
  return { order: cycle ? null : order, cycle };
};

const SEMANTIC_TO_LEGACY = {
  GROSS_UPTO_DATE: 'upto_date_gross_value_of_work_done',
  CURRENT_RA_VALUE: 'net_value_work_done_since_prev_bill',
  NET_EXCL_SECURED_ADVANCE: 'net_value_work_done_without_sec_adv',
  TOTAL_RECOVERIES: 'total_recoveries',
  TOTAL_WITHHELD: 'total_withheld',
  TOTAL_DEDUCTIONS: 'total_deductions',
  NET_PAYABLE: 'net_payment_received',
  PROGRESS_PERCENTAGE: 'percent_workdone',
  SECURITY_DEPOSIT: 'security_deposit',
  GST_DEDUCTION: 'gst_deduction',
  INCOME_TAX: 'income_tax_deduction',
  LABOUR_CESS: 'labour_cess',
  PREVIOUS_RA_GROSS: 'gross_value_as_per_previous_ra_bill',
  SECURED_ADVANCE_ADDED: 'secured_advance_added_in_this_bill',
};

export const hasConfigurableTemplateSnapshot = (billOrOpts = {}) => {
  const engine = String(
    billOrOpts.calculation_engine
      ?? billOrOpts.calculationEngine
      ?? billOrOpts.ra_template_snapshot?.calculationEngine
      ?? '',
  ).toUpperCase();
  const snap = billOrOpts.ra_template_snapshot || billOrOpts.templateSnapshot;
  return engine === 'CONFIGURABLE' && snap && Array.isArray(snap.fields) && snap.fields.length > 0;
};

export function computeRABillAmountsFromTemplate({
  templateSnapshot,
  inputs = {},
  previousValues = {},
  overrides = {},
  projectValues = {},
  agreementValues = {},
  departmentValues = {},
  cumulativeValues = {},
}) {
  const fields = (templateSnapshot.fields || []).filter(
    (f) => !['INACTIVE', 'DEPRECATED'].includes(String(f.status || 'ACTIVE').toUpperCase()),
  );
  const { order, cycle } = topoOrder(fields);
  if (cycle) {
    return {
      engine: 'CONFIGURABLE',
      error: `Circular dependency: ${cycle.join(' → ')}`,
      values: {},
      fieldValues: {},
      effectiveFieldValues: {},
    };
  }
  const fieldValues = { ...inputs };
  for (const code of (order || fields.map((f) => f.fieldCode))) {
    const field = fields.find((f) => f.fieldCode === code);
    if (!field) continue;
    const fieldType = String(field.fieldType || 'INPUT').toUpperCase();
    let calculated;
    if (fieldType === 'FORMULA' && field.formulaJson) {
      calculated = applyRounding(
        evaluateAst(field.formulaJson, {
          currentValues: fieldValues,
          previousValues,
          projectValues,
          agreementValues,
          departmentValues,
          cumulativeValues,
        }),
        field.roundingMethod,
        field.decimalPlaces,
      );
    } else if (fieldType === 'PREVIOUS_RA') {
      calculated = nz(previousValues[code] ?? field.defaultValue ?? 0);
    } else {
      calculated = applyRounding(
        nz(inputs[code] ?? field.defaultValue ?? fieldValues[code] ?? 0),
        field.roundingMethod,
        field.decimalPlaces,
      );
    }
    fieldValues[code] = calculated;
  }
  const effectiveFieldValues = { ...fieldValues };
  Object.keys(overrides || {}).forEach((code) => {
    const field = fields.find((f) => f.fieldCode === code);
    if (!field?.allowOverride) return;
    if (overrides[code] == null || overrides[code] === '') return;
    effectiveFieldValues[code] = nz(overrides[code]);
  });

  const legacy = {};
  fields.forEach((f) => {
    const role = f.semanticRole ? String(f.semanticRole) : '';
    const key = SEMANTIC_TO_LEGACY[role];
    if (!key) return;
    let v = effectiveFieldValues[f.fieldCode];
    if (role === 'PROGRESS_PERCENTAGE' && v > 0 && v <= 1) v = Number((v * 100).toFixed(2));
    legacy[key] = v;
  });
  if (legacy.net_payment_received != null) legacy.net_payable = legacy.net_payment_received;
  if (legacy.upto_date_gross_value_of_work_done != null) {
    legacy.upto_date_gross_inc_sec_adv = legacy.upto_date_gross_value_of_work_done;
  }
  return {
    engine: 'CONFIGURABLE',
    values: legacy,
    fieldValues,
    effectiveFieldValues,
    legacy,
  };
}

/**
 * Drop-in router used by RABillsRegister.
 * Same signature shape as computeCpwdRABillAmounts + optional template opts.
 */
export function computeRABillAmounts(opts = {}) {
  const billMeta = opts.billMeta || opts.bill || {};
  if (hasConfigurableTemplateSnapshot({ ...billMeta, ...opts })) {
    const snap = opts.templateSnapshot || billMeta.ra_template_snapshot;
    const result = computeRABillAmountsFromTemplate({
      templateSnapshot: snap,
      inputs: opts.templateInputs || {},
      previousValues: opts.previousTemplateValues || {},
      overrides: opts.templateOverrides || {},
      projectValues: opts.projectValues || {},
      agreementValues: opts.agreementValues || {},
      departmentValues: opts.departmentValues || {},
      cumulativeValues: opts.cumulativeValues || {},
    });
    if (result.error) {
      // Safe fallback — never break register
      const cpwd = computeCpwdRABillAmounts(opts);
      return toLegacyFormulaShape(cpwd, { agreementAmount: opts.agreementAmount });
    }
    const mapped = {
      ...toLegacyFormulaShape(
        {
          upto_date_gross_value_of_work_done: result.legacy.upto_date_gross_value_of_work_done || 0,
          gross_value_as_per_previous_ra_bill: result.legacy.gross_value_as_per_previous_ra_bill || 0,
          net_value_of_work_done_since_previous_bill: result.legacy.net_value_work_done_since_prev_bill || 0,
          secured_advance_added_in_this_bill: result.legacy.secured_advance_added_in_this_bill || 0,
          net_value_of_work_done_excluding_secured_advance: result.legacy.net_value_work_done_without_sec_adv || 0,
          security_deposit_2_5_percent: result.legacy.security_deposit || 0,
          gst_2_percent: result.legacy.gst_deduction || 0,
          income_tax_2_percent: result.legacy.income_tax_deduction || 0,
          labour_cess_1_percent: result.legacy.labour_cess || 0,
          addl_sd_for_special_items: result.legacy.additional_sd_special_items || 0,
          total_recoveries: result.legacy.total_recoveries || 0,
          total_withheld_amounts: result.legacy.total_withheld || 0,
          total_deductions: result.legacy.total_deductions || 0,
          net_payment_received: result.legacy.net_payment_received || 0,
          progress_percent: (Number(result.legacy.percent_workdone) || 0) / 100,
          upto_date_gross_inc_sec_adv: result.legacy.upto_date_gross_inc_sec_adv || 0,
          previous_ra_gross_inc_sec_adv: result.legacy.gross_value_as_per_previous_ra_bill || 0,
        },
        { agreementAmount: opts.agreementAmount },
      ),
      _templateEngine: 'CONFIGURABLE',
      _templateFieldValues: result.effectiveFieldValues,
    };
    return mapped;
  }

  const cpwd = computeCpwdRABillAmounts(opts);
  return toLegacyFormulaShape(cpwd, { agreementAmount: opts.agreementAmount });
}

export { extractCommercialInputs, computeCpwdRABillAmounts, toLegacyFormulaShape };
